import React, { useEffect, useState } from "react";

// serverNowIso is the moment the server computed its snapshot for. Counting
// against it (instead of this device's clock) keeps the countdown right even
// when the device clock is off or the page is previewing another date.
function useCountdown(targetIso, serverNowIso) {
  const [text, setText] = useState("-");

  useEffect(() => {
    if (!targetIso) {
      setText("-");
      return undefined;
    }
    const target = new Date(targetIso).getTime();
    const offset = serverNowIso ? new Date(serverNowIso).getTime() - Date.now() : 0;
    const tick = () => {
      const s = Math.max(0, Math.floor((target - (Date.now() + offset)) / 1000));
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      setText(h > 0 ? `${h}h ${m}m ${sec}s` : `${m}m ${sec}s`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [targetIso, serverNowIso]);

  return text;
}

function SantaInfo({ info }) {
  const countdown = useCountdown(info.nextArriveAt, info.serverNow);
  const delivering = info.phase === "delivering";
  const where = info.city || "North Pole";

  return (
    <div className="fd-card">
      <div className="fd-card-header">
        <h3 className="fd-card-title">FLIGHT DETAILS</h3>
      </div>

      <div className="fd-card-body fd-flight-info">
        <div>
          <div className="fd-flight-title">🎅 Santa Claus</div>
          <div className="fd-aircraft">Sleigh (8 reindeer + 1 red nose)</div>
        </div>

        <h4 className="fd-section">Right now</h4>
        <div className="fd-row">
          {delivering ? "Currently: " : "Last stop: "}
          {where}
        </div>
        <div className="fd-row">
          Status: {delivering ? "Delivering presents" : "In flight"}
        </div>

        <h4 className="fd-section">Next stop</h4>
        <div className="fd-row">City: {info.nextCity || "Back to the North Pole"}</div>
        <div className="fd-row">Arriving in: {countdown}</div>

        <h4 className="fd-section">Progress</h4>
        <div className="fd-row">
          Stops completed: {info.stopsDone ?? "-"} / {info.totalStops ?? "-"}
        </div>
      </div>
    </div>
  );
}

function FlightInfo({ info }) {
  if (!info) return null;
  if (info.source === "santa") return <SantaInfo info={info} />;

  const { flight, airline, aircraft, departure, arrival } = info;

  const format = (t) =>
    t ? new Date(t).toLocaleString("en-US", { hour: "2-digit", minute: "2-digit" }) : "-";

  return (
    <div className="fd-card">
      <div className="fd-card-header">
        <h3 className="fd-card-title">FLIGHT DETAILS</h3>
      </div>

      <div className="fd-card-body fd-flight-info">
        <div>
          <div className="fd-flight-title">
            {airline?.name} – {flight?.iata}
          </div>
          <div className="fd-aircraft">
            Aircraft: {aircraft?.model || aircraft?.reg || "Unknown"}
          </div>
        </div>

        <h4 className="fd-section">Departure</h4>
        <div className="fd-row">Airport: {departure?.iata} – {departure?.airport}</div>
        <div className="fd-row">Scheduled: {format(departure?.scheduled)}</div>
        <div className="fd-row">Actual: {format(departure?.actual)}</div>
        <div className="fd-row">Terminal: {departure?.terminal || "-"}</div>
        <div className="fd-row">Gate: {departure?.gate || "-"}</div>

        <h4 className="fd-section">Arrival</h4>
        <div className="fd-row">Airport: {arrival?.iata} – {arrival?.airport}</div>
        <div className="fd-row">Scheduled: {format(arrival?.scheduled)}</div>
        <div className="fd-row">Estimated: {format(arrival?.estimated)}</div>
        <div className="fd-row">Terminal: {arrival?.terminal || "-"}</div>
        <div className="fd-row">Baggage: {arrival?.baggage || "-"}</div>

        <h4 className="fd-section">Progress</h4>
        <div className="fd-row">Time Remaining: {info.timeRemaining || "-"}</div>
      </div>
    </div>
  );
}

export default FlightInfo;
